<template>
  <AnimatedBar :showLeft="showBackButton">
    <template #left>
      <ButtonText
        id="feature-navigate-back"
        class="text-button"
        :iconLeft="iconChevronLeft"
        size="extra-small"
        @onClicked="$emit('goBack')"
      />
    </template>
    <template #middle>
      <TextBase :text="title.replace('&shy;', '')" type="h4" weight="bold" />
      <TooltipStandardV2
        v-if="info"
        :style="'margin-left: 5px; margin-top: 5px;'"
        :text="info"
        :arrowPosition="!fullscreen ? 'top-center' : 'center-right'"
        :verticalOffset="!fullscreen ? 4 : 1"
        :horizontalOffset="!fullscreen ? 0 : 2"
      >
        <IconSmallInfo class="icon" />
      </TooltipStandardV2>
    </template>
    <template #right>
      <ButtonText
        v-if="piniaStore.showFeatureAnalytic && enablePrintReport"
        id="button-create-report"
        class="text-button"
        :iconLeft="iconFileTextSmallBlue"
        size="extra-small"
        @onClicked="printReport"
      />
      <ButtonText
        v-if="showResizeButton"
        id="feature-toggle-fullscreen"
        class="text-button"
        :iconLeft="fullscreen ? iconMinimize2 : iconMaximize2"
        size="extra-small"
        @onClicked="$emit('toggleFullscreen')"
      />
      <ButtonText
        id="feature-exit"
        class="text-button"
        :iconLeft="iconXSmall"
        size="extra-small"
        @onClicked="$emit('exit')"
      />
    </template>
  </AnimatedBar>
</template>
<script lang="ts" setup>
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { useGeneralStore } from '@/pinia'
import { useDataSources } from '@/composables/useDataSources'
import { useFeature } from '@/pinia/features'
import { useButtonTextFormatter } from '@/components/AnalyticsDashboard/components/CollapseController/composables'
import { LOCALES } from '@/utils/locales'
import usePdfReport from '@/components/AnalyticsDashboard/usePdfReport'
import ButtonText from '@/design-system/atoms/buttons/ButtonText.vue'
import AnimatedBar from './AnimatedBar.vue'
import IconSmallInfo from '../assets/icons/IconSmallInfo.vue'
import TooltipStandardV2 from '../atoms/TooltipStandardV2.vue'
import TextBase from '../atoms/TextBase.vue'
import { useSelectionStore } from '@/pinia/selectionNew'
import iconChevronLeft from '@/design-system/assets/icons/Icon-Chevron-Left.svg'
import iconFileTextSmallBlue from '@/design-system/assets/icons/Icon-File-Text-Small-Blue.svg'
import iconMaximize2 from '@/design-system/assets/icons/Icon-Maximize-2.svg'
import iconMinimize2 from '@/design-system/assets/icons/Icon-Minimize-2.svg'
import iconXSmall from '@/design-system/assets/icons/Icon-X-Small.svg'
type Props = {
  title: string
  fullscreen?: boolean
  showResizeButton?: boolean
  showBackButton?: boolean
  info?: string
}
type Emits = {
  (e: 'exit'): void
  (e: 'toggleFullscreen'): void
  (e: 'goBack'): void
}
const props = defineProps<Props>()
defineEmits<Emits>()

const piniaStore = useGeneralStore()
const store = useStore()
const { locale } = useI18n()
const { buttonText: dateInLocaleString } = useButtonTextFormatter(LOCALES[locale.value])
const { dataSources } = storeToRefs(useDataSources())
const { feature: currentSection } = useFeature('home/*/*/*/data/*')
const selectionStore = useSelectionStore()
const { generatePdfReport } = usePdfReport()

const allBuildings = computed<object[]>(() => store.getters.allBuildings)
const clickedItemType = computed<string>(() => store.state.clickedItemType)
const enablePrintReport = computed<boolean>(
  () => currentSection.value?.parent?.parent?.name !== undefined
)
const realmLogo = computed<string>(() => store.getters.realmLogo)
const title = computed<string>(() => props.title.replace('&shy;', ''))

function printReport() {
  const kpis = currentSection.value?.children
    ?.filter((c) => c.component === 'MainMeasurementCard')
    .map((c) => ({
      dataSource: dataSources.value[c.props.value.dataSource],
      config: c
    }))
  const coloredEntities = currentSection.value?.children
    ?.filter((c) => c.component === 'MainMeasurementCard')
    .map((c) => ({
      dataSource: dataSources.value[c.props.coloredEntities.dataSource],
      config: c
    }))
  const entitySourceBuildings = allBuildings.value
  const entitySourceLevels = store.state.levelsForSelectedBuilding?.data?.features ?? []
  const entitySourceRooms = store.state.roomsForSelectedLevel?.data?.features ?? []
  const entitySource = entitySourceBuildings.concat(entitySourceLevels).concat(entitySourceRooms)
  const titleReport = `${currentSection.value?.parent?.parent?.title[locale.value].replaceAll(
    '&shy;',
    ''
  )} - ${currentSection.value?.title[locale.value].replaceAll('&shy;', '')}`
  generatePdfReport(
    titleReport ?? '',
    currentSection.value?.parent?.parent?.name,
    selectionStore.entityPath,
    dateInLocaleString.value,
    kpis,
    piniaStore.selectedAnalysisKPI,
    coloredEntities,
    entitySource,
    clickedItemType.value,
    realmLogo.value
  )
}
</script>

<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/variables.scss';
.title {
  margin: 0;
  @include font-mixins.desktop-bold-h4;
  color: variables.$color-greyscale-content;
  word-break: break-all;
}

.text-button {
  width: 24px;
  height: 24px;
}
.icon {
  color: variables.$color-greyscale-secondary;
  height: 24px;
  width: 24px;
  cursor: pointer;
}
</style>
